// src/UserTaxonomyEditor.js
import React, { useEffect, useMemo, useState } from 'react';
import { getTaxonomy } from './api';
import TaxonomyMultiSelect from './TaxonomyMultiSelect';

const PROG_EI = 'PROG_EI';
const PROG_PI = 'PROG_PI';

export default function UserTaxonomyEditor({
  value = { strengths: [], limitations: [] }, // strengths:[{code,level,weight}] | ["CODE"]; limitations:[{code}] | ["CODE"]
  onChange,
  limitationCategories = ['limitation', 'behavior'],
}) {
  const [tx, setTx] = useState({});

  useEffect(() => {
    getTaxonomy().then(res => setTx(res.data || {}));
  }, []);

  // helpers
  const toCode = (x) => (typeof x === 'string' ? x : x?.code);
  const normStrength = (x) =>
    typeof x === 'string' ? { code: x, level: 'basic', weight: 1 } : { level: 'basic', weight: 1, ...x };
  const normLimitation = (x) =>
    typeof x === 'string' ? { code: x } : { ...x };

  const isProgramCode = (code) => String(code || '').toUpperCase().startsWith('PROG_');

  const catOf = (it) => {
    const code = String(it?.code || '').toUpperCase();
    const cat  = String(it?.category || '').toLowerCase();
    if (code.startsWith('PROG_')) return 'program';
    return cat || 'other';
  };

  const catRank = (cat) => {
    const c = String(cat || '').toLowerCase();
    if (c === 'program') return [0, 'program'];
    if (c === 'domain')  return [1, 'domain'];
    return [2, c || 'zz_other'];
  };

  // all items, sorted: program, domain, then alpha
  const allItems = useMemo(() => {
    const arr = [];
    for (const [, list] of Object.entries(tx || {})) {
      for (const it of list) arr.push({ ...it, __cat: catOf(it) });
    }
    return arr.sort((a, b) => {
      const [ar, an] = catRank(a.__cat);
      const [br, bn] = catRank(b.__cat);
      if (ar !== br) return ar - br;
      if (an !== bn) return an.localeCompare(bn);
      const al = (a.label || a.code || '');
      const bl = (b.label || b.code || '');
      return al.localeCompare(bl);
    });
  }, [tx]);

  const codeToItem = useMemo(() => {
    const m = new Map();
    for (const it of allItems) m.set(it.code, it);
    return m;
  }, [allItems]);

  // selected sets
  const strengthCodes = useMemo(
    () => new Set((value.strengths || []).map(toCode).filter(Boolean)),
    [value.strengths]
  );
  const limitationCodes = useMemo(
    () => new Set((value.limitations || []).map(toCode).filter(Boolean)),
    [value.limitations]
  );
  const selectedCodes = useMemo(
    () => new Set([...strengthCodes, ...limitationCodes]),
    [strengthCodes, limitationCodes]
  );

  // koliki je broj "program" kodova u strengths?
  const programStrengths = useMemo(() => {
    const arr = (value.strengths || []).map(toCode).filter(c => isProgramCode(c));
    return new Set(arr);
  }, [value.strengths]);

  // MAIN TOGGLE sa zaštitom: ne dozvoli da oba PROG_* budu nečekirana
  const handleToggle = (code) => {
    const exists = selectedCodes.has(code);
    const isProgram = isProgramCode(code);

    let strengths = (value.strengths || []).map(normStrength);
    let limitations = (value.limitations || []).map(normLimitation);

    if (exists) {
      // uklanjanje
      strengths = strengths.filter(s => s.code !== code);
      limitations = limitations.filter(l => l.code !== code);

      if (isProgram) {
        // ako se skida PROG_* iz strengths, proveri da li ostaje bar jedan PROG_*
        const stillPrograms = strengths.map(s => s.code).filter(isProgramCode);
        if (stillPrograms.length === 0) {
          // vrati ga nazad — ne dozvoli da oba budu off
          strengths = [...strengths, { code, level: 'basic', weight: 1 }];
        }
      }
    } else {
      // dodavanje
      const cat = String(codeToItem.get(code)?.category || '').toLowerCase();
      if (limitationCategories.includes(cat) && !isProgram) {
        // limitation kategorije idu u limitations (ali nikada program)
        limitations = [...limitations, { code }];
        strengths = strengths.filter(s => s.code !== code);
      } else {
        // ostalo ide u strengths (program uvek strength)
        strengths = [...strengths, { code, level: 'basic', weight: 1 }];
        limitations = limitations.filter(l => l.code !== code);
      }
    }

    onChange && onChange({ ...value, strengths, limitations });
  };

  // premeštanje strength/limitation — program NE SME u limitation i ne sme ostati bez ijednog
  const changeType = (code, type) => {
    const isProgram = isProgramCode(code);
    let strengths = (value.strengths || []).map(normStrength);
    let limitations = (value.limitations || []).map(normLimitation);

    if (type === 'strengths') {
      if (!strengthCodes.has(code)) {
        strengths = [...strengths, { code, level: 'basic', weight: 1 }];
      }
      limitations = limitations.filter(l => l.code !== code);
    } else {
      // type === 'limitations'
      if (isProgram) {
        // program ne sme u limitations — abort
        return;
      }
      if (!limitationCodes.has(code)) {
        limitations = [...limitations, { code }];
      }
      strengths = strengths.filter(s => s.code !== code);

      // posle uklanjanja iz strengths, uveri se da i dalje postoji bar jedan PROG_*
      const stillPrograms = strengths.map(s => s.code).filter(isProgramCode);
      if (stillPrograms.length === 0) {
        // vrati ga nazad
        strengths = [...strengths, { code, level: 'basic', weight: 1 }];
        limitations = limitations.filter(l => l.code !== code);
      }
    }

    onChange && onChange({ ...value, strengths, limitations });
  };

  const setStrengthMeta = (code, patch) => {
    const strengths = (value.strengths || [])
      .map(normStrength)
      .map(s => (s.code === code ? { ...s, ...patch } : s));
    onChange && onChange({ ...value, strengths });
  };

  const selectedList = useMemo(() => {
    const list = [...selectedCodes].map(code => ({
      code,
      item: codeToItem.get(code),
      isStrength: strengthCodes.has(code),
      isLimitation: limitationCodes.has(code),
    }));
    // prvo strengths pa limitations, potom po label
    return list.sort((a, b) => {
      if (a.isStrength !== b.isStrength) return a.isStrength ? -1 : 1;
      const la = a.item?.label || a.code;
      const lb = b.item?.label || b.code;
      return la.localeCompare(lb);
    });
  }, [selectedCodes, strengthCodes, limitationCodes, codeToItem]);

  return (
    <div>
      {/* Uključuje program, domain i ostalo — već sortirano po pravilima */}
      <TaxonomyMultiSelect
        title="Teacher Profile (taxonomy)"
        items={allItems}
        selectedCodes={selectedCodes}
        onToggle={handleToggle}
        groupByCategory={true}
      />

      {selectedList.length > 0 && (
        <div className="mt-2 border rounded p-2">
          <div className="text-sm font-semibold mb-1">Profile details</div>
          <div className="space-y-2">
            {selectedList.map(row => {
              const label = row.item?.label || row.code;
              const sObj = (value.strengths || []).map(normStrength).find(s => s.code === row.code);
              const isProg = isProgramCode(row.code);
              return (
                <div key={row.code} className="flex flex-wrap items-center gap-2">
                  <span className="text-sm w-44 truncate" title={label}>{label}</span>

                  <label className="text-sm flex items-center gap-1">
                    <input
                      type="radio"
                      name={`type-${row.code}`}
                      checked={row.isStrength}
                      onChange={() => changeType(row.code, 'strengths')}
                    />
                    Strength
                  </label>

                  <label className={`text-sm flex items-center gap-1 ${isProg ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name={`type-${row.code}`}
                      checked={row.isLimitation}
                      onChange={() => !isProg && changeType(row.code, 'limitations')}
                      disabled={isProg}
                    />
                    Limitation
                  </label>

                  {row.isStrength && (
                    <>
                      <select
                        value={sObj?.level || 'basic'}
                        onChange={e => setStrengthMeta(row.code, { level: e.target.value })}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        <option value="basic">Basic</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>

                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={sObj?.weight ?? 1}
                        onChange={e =>
                          setStrengthMeta(
                            row.code,
                            { weight: Math.max(1, Math.min(10, Number(e.target.value || 1))) }
                          )
                        }
                        className="border rounded px-2 py-1 text-sm w-20"
                        placeholder="Weight"
                        title="Weight (1-10)"
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


